import React, { memo, useCallback, useMemo } from 'react'
import {
  OFFER_TYPE_TOUR_V2,
} from 'constants/offer'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import { buildSearchStringFromFilters } from 'lib/search/searchUtils'

import Heading from 'components/Luxkit/Typography/Heading'
import useOfferList from 'hooks/Offers/useOfferList'
import { EmptyObject } from 'lib/object/objectUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import { rem } from 'polished'
import styled from 'styled-components'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { OptimizelyServerSideExperiments, getOptimizelyExperimentVariation, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { useLereTourRecommendation } from 'hooks/Recommendation/useLereTourRecommendation'
import { useInView } from 'react-intersection-observer'
import * as Analytics from 'analytics/analytics'
import { clickTourOfferPageRecommendation, viewTourOfferPageRecommendation } from 'analytics/optimizely/events'

const RecommendationsSection = styled(Pane)`
  padding: ${rem(40)} 0;
`

const emptyFilters: App.OfferListFilters = EmptyObject

interface Props {
  offerId: string;
  filters?: App.OfferListFilters;
  trackingDescription: string;
  placeIds: string[];
}

const MINIMUM_OFFERS_TO_DISPLAY = 4
const RECOMMENDATION_TYPES: App.OfferParentType[] = [
  OFFER_TYPE_TOUR_V2,
]

const AB_TEST = OptimizelyServerSideExperiments.lereTourRecommendation

const clickEvent = clickTourOfferPageRecommendation()
const viewEvent = viewTourOfferPageRecommendation()

function TourV2YouMayAlsoLikeCarrousel(props: Props) {
  const {
    offerId,
    filters = emptyFilters,
    trackingDescription,
    placeIds,
  } = props

  const canUseLereReco = useAppSelector(state => getOptimizelyExperimentVariation(state, AB_TEST) === 'test')

  // not using occupancy/date yet
  // always fetch reco as we need that to decide whether user can be included in test
  const lereReco = useLereTourRecommendation(true, offerId)
  const lereLoading = !lereReco || lereReco.state === 'loading'
  const lereVersion = lereReco?.lereVersion
  const hasValidLereReco = (lereReco?.offers?.length ?? 0) >= MINIMUM_OFFERS_TO_DISPLAY
  const shouldUseLereReco = canUseLereReco && (lereLoading || hasValidLereReco)

  const defaultOfferList = useOfferList({
    ...filters,
    offerTypes: RECOMMENDATION_TYPES,
    locations: placeIds,
    offerIdsToExclude: [offerId],
  })

  // only user who have both variants can be subjected to the AB test
  const bothVariantHasValidReco = hasValidLereReco && defaultOfferList.offerIds.length >= MINIMUM_OFFERS_TO_DISPLAY

  const offerList = useMemo(() => {
    if (shouldUseLereReco) {
      return {
        offerIds: lereReco?.offers?.map(offer => offer.offerId) ?? [],
        key: 'lereTourReco',
        fetching: lereLoading,
      }
    }
    return defaultOfferList
  }, [shouldUseLereReco, lereReco, lereLoading, defaultOfferList])

  const offerSource = useMemo(() => {
    return shouldUseLereReco ? `LERE - tours - ${lereVersion}` : 'Search - tours'
  }, [lereVersion, shouldUseLereReco])

  const tracking = useOfferListTracking(
    `Recommendations-Search Nearby-${trackingDescription}`,
    {
      ga: {
        category: 'Recommendations',
        label: `Search Nearby-${trackingDescription}`,
      },
      source: offerSource,
    },
  )

  const additionalQueryParams = useMemo(() => buildSearchStringFromFilters(filters), [filters])

  const [ref, inView] = useInView({ triggerOnce: true })
  // only trigger experiment if both variants are valid
  useOptimizelyExperiment(AB_TEST, inView && bothVariantHasValidReco)

  const onListEvent = useCallback((action: OfferListEvents) => {
    if (bothVariantHasValidReco) {
      if (action === OfferListEvents.productClick) {
        Analytics.track(clickEvent.eventName, clickEvent)
      }
      if (action === OfferListEvents.impression) {
        // The visitor event and offer view event almost fires at the same time when the carousel come into view
        // but if offer view event is before visitor event it won't count in optimizely
        // so we have to delay offer view event to avoid race condition with experiment trigger
        setTimeout(() => Analytics.track(viewEvent.eventName, viewEvent), 500)
      }
    }
  }, [bothVariantHasValidReco])

  if (!offerList.fetching && offerList.offerIds.length < MINIMUM_OFFERS_TO_DISPLAY) {
    return null
  }

  return (
    <OfferListEventsProvider tracking={tracking} onListEvent={onListEvent}>
      <RecommendationsSection ref={ref}>
        <VerticalSpacer gap={16}>
          <LayoutContainer>
            <Heading variant="heading3">You may also like</Heading>
          </LayoutContainer>
          <OfferListCarousel
            overrideOfferList={offerList}
            additionalOfferUrlQuery={additionalQueryParams}
          />
        </VerticalSpacer>
      </RecommendationsSection>
    </OfferListEventsProvider>
  )
}

export default memo(TourV2YouMayAlsoLikeCarrousel)
